import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  X, Megaphone, Sparkles, Gift, Bell, Star, Rocket, Heart,
  Zap, Music, PartyPopper, Trophy, Info, Calendar, Volume2,
  TrendingUp, Award, Flame, Crown, Mic2,
} from 'lucide-react';

const ICON_MAP = {
  Megaphone, Sparkles, Gift, Bell, Star, Rocket, Heart,
  Zap, Music, PartyPopper, Trophy, Info, Calendar, Volume2,
  TrendingUp, Award, Flame, Crown, Mic2,
};

const THEMES = {
  blue: { accent: '#38bdf8', glow: '#38bdf8' },
  purple: { accent: '#a78bfa', glow: '#a78bfa' },
  pink: { accent: '#f472b6', glow: '#f472b6' },
  green: { accent: '#34d399', glow: '#34d399' },
  orange: { accent: '#fbbf24', glow: '#fbbf24' },
  dark: { accent: '#cbd5e1', glow: '#94a3b8' },
};

function isDismissed(id) {
  const raw = localStorage.getItem(`popup_dismiss_${id}`);
  if (!raw) return false;
  try {
    const { until } = JSON.parse(raw);
    if (Date.now() < until) return true;
    localStorage.removeItem(`popup_dismiss_${id}`);
    return false;
  } catch {
    localStorage.removeItem(`popup_dismiss_${id}`);
    return false;
  }
}

function dismiss(id, hours) {
  localStorage.setItem(
    `popup_dismiss_${id}`,
    JSON.stringify({ until: Date.now() + hours * 60 * 60 * 1000 })
  );
}

const isPaperDomain =
  window.location.hostname === 'pinkpaper.kr' ||
  window.location.hostname === 'www.pinkpaper.kr';

const isKdistDomain =
  window.location.hostname === 'kdist.net' ||
  window.location.hostname === 'www.kdist.net' ||
  window.location.hostname.endsWith('.kdist.net');

export default function PopupModal() {
  const [popups, setPopups] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { pathname } = useLocation();

  // 핑크페이퍼/kdist 도메인에서는 팝업 표시하지 않음
  // 메인 페이지에서만 팝업 표시: 홈페이지(/, /public), 정산(dashboard)
  const isPaperPath = pathname === '/paper' || pathname.startsWith('/paper/');
  const isHomepageMain = !isPaperDomain && !isKdistDomain && !isPaperPath && (pathname === '/' || pathname === '/public');
  const isSettlementMain = pathname === '/dashboard';
  const targetPage = isSettlementMain ? 'settlement' : isHomepageMain ? 'homepage' : null;

  const fetchPopups = useCallback(async () => {
    if (!targetPage) { setPopups([]); return; }
    try {
      const res = await axios.get('/api/popup/active', { params: { target: targetPage } });
      const active = (res.data || []).filter((p) => !isDismissed(p.id));
      setPopups(active);
      setCurrentIndex(0);
    } catch {
      // ignore
    }
  }, [targetPage]);

  useEffect(() => {
    fetchPopups();
  }, [fetchPopups, pathname]);

  const handleDismiss = (hours) => {
    const popup = popups[currentIndex];
    if (!popup) return;
    dismiss(popup.id, hours);
    const next = popups.filter((_, i) => i !== currentIndex);
    setPopups(next);
    setCurrentIndex((prev) => Math.min(prev, next.length - 1));
  };

  const handleClose = () => {
    const next = popups.filter((_, i) => i !== currentIndex);
    setPopups(next);
    setCurrentIndex((prev) => Math.min(prev, next.length - 1));
  };

  if (!targetPage || popups.length === 0) return null;

  const popup = popups[currentIndex];
  if (!popup) return null;

  const theme = THEMES[popup.theme] || THEMES.blue;
  const IconComp = (popup.icon && ICON_MAP[popup.icon]) || Megaphone;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Glass Modal */}
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden animate-[fadeIn_0.3s_ease-out]"
        style={{
          background: 'rgba(255, 255, 255, 0.06)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 80px ${theme.glow}10, inset 0 1px 0 rgba(255, 255, 255, 0.1)`,
        }}
      >
        {/* Top accent glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}80, transparent)` }}
        />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition-colors z-10"
        >
          <X size={18} />
        </button>

        {/* Content */}
        <div className="px-7 pt-7 pb-5">
          {/* Icon + Counter row */}
          <div className="flex items-start justify-between mb-5">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${theme.accent}20, ${theme.accent}08)`,
                border: `1px solid ${theme.accent}25`,
                boxShadow: `0 4px 16px ${theme.accent}15`,
              }}
            >
              <IconComp size={26} style={{ color: theme.accent }} />
            </div>
            {popups.length > 1 && (
              <span
                className="px-2.5 py-1 rounded-full text-[10px] font-bold"
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                {currentIndex + 1} / {popups.length}
              </span>
            )}
          </div>

          <h2 className="text-xl font-bold text-white leading-tight mb-2">
            {popup.title}
          </h2>

          {/* Accent bar */}
          <div
            className="w-8 h-[3px] rounded-full mb-4"
            style={{ background: `linear-gradient(90deg, ${theme.accent}, ${theme.accent}40)` }}
          />

          <div className="text-white/60 text-sm leading-relaxed whitespace-pre-wrap max-h-[40vh] overflow-y-auto pr-1">
            {popup.content}
          </div>
        </div>

        {/* Bottom buttons */}
        <div
          className="px-7 py-4 flex items-center gap-2"
          style={{
            background: 'rgba(0, 0, 0, 0.25)',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <button
            onClick={() => handleDismiss(24)}
            className="flex-1 px-3 py-2.5 rounded-xl text-xs font-medium text-white/60 hover:text-white/90 transition-colors"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            오늘 그만보기
          </button>
          <button
            onClick={() => handleDismiss(24 * 7)}
            className="flex-1 px-3 py-2.5 rounded-xl text-xs font-medium text-white/60 hover:text-white/90 transition-colors"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            7일간 안보기
          </button>
          <button
            onClick={handleClose}
            className="flex-1 px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors"
            style={{
              background: `${theme.accent}30`,
              color: theme.accent,
              border: `1px solid ${theme.accent}40`,
            }}
          >
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
